import { useCallback, useEffect, useRef, useState } from "react";
import { Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { DialogRequest, DialogTone, registerDialogHost } from "@/shared/services/dialogService";

const ICONS: Record<DialogTone, { icon: string; color: string }> = {
  primary: { icon: "bx bx-help-circle", color: "text-primary" },
  warning: { icon: "bx bx-error", color: "text-warning" },
  danger: { icon: "bx bx-error-circle", color: "text-danger" },
};

// Draws the dialogs asked with `confirmDialog` / `promptDialog` (see dialogService.ts). Mount it once, at the
// root of the app. Dialogs asked while another is open wait their turn.
const DialogHost = () => {
  const [queue, setQueue] = useState<DialogRequest[]>([]);
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const current = queue[0] ?? null;

  useEffect(() => registerDialogHost((request) => setQueue((previous) => [...previous, request])), []);

  useEffect(() => {
    setText(current?.kind === "prompt" ? (current.options.defaultValue ?? "") : "");
  }, [current]);

  const close = useCallback((answer: boolean) => {
    setQueue((previous) => {
      const [first, ...rest] = previous;

      if (first?.kind === "confirm") first.resolve(answer);
      // A prompt answers with its text when accepted and null when cancelled.
      return rest;
    });
  }, []);

  const closePrompt = useCallback(
    (accepted: boolean) => {
      setQueue((previous) => {
        const [first, ...rest] = previous;

        if (first?.kind === "prompt") first.resolve(accepted ? text : null);

        return rest;
      });
    },
    [text],
  );

  if (!current) return null;

  const { options } = current;
  const tone = options.tone ?? "primary";
  const accept = () => (current.kind === "prompt" ? closePrompt(true) : close(true));
  const cancel = () => (current.kind === "prompt" ? closePrompt(false) : close(false));

  return (
    <Modal isOpen centered toggle={cancel} onOpened={() => inputRef.current?.focus()}>
      <ModalHeader toggle={cancel}>
        <i className={`${ICONS[tone].icon} ${ICONS[tone].color} me-2`} />
        {options.title ?? "Confirmar"}
      </ModalHeader>
      <ModalBody>
        <div style={{ whiteSpace: "pre-line" }}>{options.message}</div>
        {current.kind === "prompt" && (
          <div className="mt-3">
            {current.options.label && <Label className="form-label">{current.options.label}</Label>}
            <Input
              innerRef={inputRef as never}
              value={text}
              placeholder={current.options.placeholder}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") accept();
              }}
            />
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-light" onClick={cancel}>
          {options.cancelLabel ?? "Cancelar"}
        </button>
        <button type="button" className={`btn btn-${tone}`} onClick={accept}>
          {options.confirmLabel ?? (current.kind === "prompt" ? "Aceptar" : "Confirmar")}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default DialogHost;
